import React, { useEffect, useMemo, useState } from "react";
import {
  Tabs,
  Table,
  Select,
  Space,
  Typography,
  DatePicker,
  Tag,
  Statistic,
  Card,
  Grid,
  Segmented,
  Popover,
  Checkbox,
  Button,
  List,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { formatDate } from "../../utils/formatDate";
import { formatPlataforma } from "../../utils/plataforma.utils";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { formatPhoneDisplay } from "../../utils/formatPhone";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import { ESTADIA_LABEL, ESTADIA_TOOLTIP } from "../../utils/estadiaCode";
import { useFinanzasClientesLogic } from "./useFinanzasClientesLogic";
import { useExpensesSection } from "./useExpensesSection";
import { useProductosSection } from "./useProductosSection";
import { useArriendosSection } from "./useArriendosSection";
import { useAseosSection } from "./useAseosSection";
import { useLavadosSection } from "./useLavadosSection";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import "./finanzasClientes.css";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { HeaderWithInfo } from "./HeaderColumn";
import { totalLabelFor } from "./sections";
import {
  MoneyChip,
  money,
  catColor,
  ListCol,
  buildJsonbGroup,
  pretty,
  DATE_KEYS,
  CLP_KEYS,
  USD_KEYS,
  ADMIN_DETAILED_KEYS,
  ADMIN_SUMMARY_KEYS,
  isJsonbListKey,
  moneyKindFor,
  isOwnerTotal,
  CompactTitle,
} from "./finanzasClientes.helpers";
import {
  SettingOutlined,
  ColumnHeightOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { ArriendoRow } from "./useArriendosSection";
import { useFinanzasTab } from "./useFinanzasTab";
import FinanzasTab from "./FinanzasTab";
import { ahoraChile } from "../../utils/fechaChile";
import { paginacionServidor } from "../../utils/tablaPaginacion";
import DescargarResumen from "./DescargarResumen";

const FinanzasClientes: React.FC = () => {
  const screens = Grid.useBreakpoint();
  // selectedClientId: estado provisorio para futura selección manual de
  // cliente desde admin. Por ahora siempre `null` — admin opera con su
  // propio user.id (ver `clientId` resuelto más abajo). El setter se
  // descarta con destructuring vacío hasta que el flujo se active.
  const [selectedClientId] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs | null>(null);
  // Round 2026-04-30 sexies — el toggle "Simular vista cliente" se removió.
  // En la práctica no aportaba (el cliente y el admin ven los mismos rows
  // porque el flag `visibleCliente` no excluye nada hoy en día) y agregaba
  // un banner ruidoso al tope del panel. Si en el futuro se necesita
  // verificar la vista del cliente, abrir issue para repensarlo desde cero.
  const {
    loadingProps,
    propsList,
    selectedPropId,
    onChangeProp,
    sidebarOpen,
    toggleSidebar,
    getPropsByClient,
    isAdmin,
    user,
  } = useFinanzasClientesLogic(selectedClientId ?? undefined);

  // Opciones AntD-shape memoizadas. Antes se hacian inline en JSX y forzaban
  // re-render del Select en cada render padre.
  const propsSelectOptions = useMemo(
    () =>
      propsList.map((p: any) => ({
        value: p.id,
        label: p.nombrepropiedad
          ? formatPropertyName(p.nombrepropiedad)
          : `Propiedad #${p.id}`,
      })),
    [propsList],
  );

  useEffect(() => {
    if (!selectedMonth) setSelectedMonth(ahoraChile());
  }, []);

  const gotoPrevMonth = () => {
    setSelectedMonth((m) =>
      m ? m.subtract(1, "month") : ahoraChile().subtract(1, "month"),
    );
  };

  // clientId que viaja en la URL `/finanzas/gastos/:clientId`.
  //  • Cliente: su propio user.id (el backend filtra a sus propiedades).
  //  • Admin/superadmin: el backend IGNORA este id para el rol admin (filtra
  //    por `propId`, no por cliente), pero el frontend exige que `clientId`
  //    sea truthy para disparar el fetch (ver useExpensesSection L80/L105:
  //    `if (!clientId || !selectedPropId) return`). Como la selección manual
  //    de cliente todavía no existe (`selectedClientId` siempre null),
  //    caíamos en `clientId = null` y el panel admin NO cargaba NINGÚN flujo
  //    aunque se eligiera propiedad y período. Fallback a `user.id` (admin)
  //    para que el fetch dispare; el backend resuelve por `propId`.
  //    Round 2026-06-05 — fix del bug "admin no ve flujos de la propiedad".
  const clientId = isAdmin
    ? (selectedClientId ?? user?.id ?? null)
    : (user?.id ?? null);
  // Aseos = aseos de CALENDARIO (tabla `aseos`/Cleaning) vía endpoint
  // dedicado. Reparaciones se eliminó (round 2026-06-05): sus flujos ahora
  // viven en "Otros" junto con todos los demás flujos de /flujos.
  const aseos = useAseosSection(clientId, selectedPropId);
  // Lavandería del cliente (AC7): SOLO lavados aledaños que él paga
  // (booking_id NULL + pagado_por.tipo=1). Los lavados de arriendo ya van en
  // la limpieza descontada → no se muestran. Ver flujo-financiero.md §7.6.
  const lavados = useLavadosSection(clientId, selectedPropId);
  const productos = useProductosSection(clientId, selectedPropId);
  const otros = useExpensesSection(clientId, selectedPropId, "otros");
  const arr = useArriendosSection(selectedPropId);
  const finanzasTab = useFinanzasTab(clientId, selectedPropId, selectedMonth);

  useEffect(() => {
    if (!selectedMonth) return;
    aseos.setMonth(selectedMonth);
    lavados.setMonth(selectedMonth);
    productos.setMonth(selectedMonth);
    otros.setMonth(selectedMonth);
    arr.setMonth(selectedMonth);
  }, [selectedMonth]);

  useEffect(() => {
    if (!isAdmin) return;
    onChangeProp(undefined as any);
    if (getPropsByClient && selectedClientId)
      getPropsByClient(selectedClientId);
  }, [selectedClientId]);

  // Mapeo de `tipo` (numérico) → etiqueta legible cuando el JSONB no trae
  // `nombre`. Los códigos coinciden con docs/flujo-financiero.md §10.2.
  //
  // tipo=4 es "Trabajador" (empleado sin login: mantención, supervisor,
  // contador). Se rotulaba "Otro" hasta el round 2026-04-30 decies; ese
  // nombre quedó obsoleto y además chocaba visualmente con el receptor
  // semilla llamado "Otro", que es tipo=5: en "Pagado por"/"Pagado a"
  // aparecían dos filas "Otro" siendo contrapartes distintas.
  //
  // ⚠️ El rótulo de tipo=5 ("Receptor externo") vale SOLO porque las filas
  // que pasan por acá salen de `gastos` (pestaña "Otros" y aseos). Ahí
  // tipo=5 = otra empresa que recibe plata de AF (internet, agua,
  // impuestos, un proveedor, o el receptor semilla "Otro"), siempre con
  // `recipient_id` → `expense_recipients.id`, y NUNCA una lavandería. En
  // `lavados` el mismo número significa otra cosa (lavandería del catálogo,
  // AF interna o General Externa, vía `usuario_id` → `lavanderias.id`), así
  // que no copies este mapa a una vista de lavandería. Este panel no lista
  // lavados con estas columnas. Ver utils/financialFlow.ts y
  // docs/flujo-financiero.md §10.7.
  const PARTY_TIPO_LABEL: Record<number, string> = {
    0: "Aloja Fácil",
    1: "Cliente",
    2: "Arrendatario",
    3: "Aseador",
    4: "Trabajador",
    5: "Receptor externo",
  };

  // pagado_por/pagado_a llegan como JSONB `{tipo, nombre?, recipient_id?}`.
  // Antes la columna usaba dataIndex sin render y AntD acababa pintando
  // `[object Object]`. Ahora extraemos el nombre vigente y caemos al label
  // del tipo si no hay snapshot.
  const renderParty = (p: any) => {
    if (!p || typeof p !== "object") {
      // Compatibilidad con datos legacy donde se persistió un string libre.
      return p ? String(p) : "—";
    }
    const nombre = p.nombre && String(p.nombre).trim();
    const tipoLabel = PARTY_TIPO_LABEL[p.tipo] ?? "—";
    return nombre ? `${nombre} (${tipoLabel})` : tipoLabel;
  };

  const expenseCols: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "fecha",
        width: 150,
        render: (v: any) => formatDate(v),
        fixed: screens.md ? "left" : undefined,
      },
      {
        title: "Categoría",
        dataIndex: "categoria",
        width: 140,
        render: (c: string) => <Tag color={catColor[c] || "default"}>{c}</Tag>,
      },
      {
        title: "Monto",
        dataIndex: "monto",
        width: 140,
        align: "right",
        render: (v: number) => money(v),
      },
      // "Pagado por" / "Pagado a" son columnas de ADMINISTRACIÓN: describen el
      // circuito interno del dinero (quién le transfiere a quién, con Aloja
      // Fácil de por medio). Al cliente no le aportan —desde el round
      // 2026-07-28 el backend solo le manda flujos donde él es una de las dos
      // puntas, así que una de las dos columnas diría siempre su propio
      // nombre— y en cambio invitan a leer el circuito completo como si fueran
      // cargos distintos. Pedido explícito del dueño.
      ...(isAdmin
        ? ([
            {
              title: "Pagado por",
              dataIndex: "pagado_por",
              width: 180,
              ellipsis: true,
              responsive: ["md"],
              render: renderParty,
            },
            {
              title: "Pagado a",
              dataIndex: "pagado_a",
              width: 180,
              ellipsis: true,
              responsive: ["lg"],
              render: renderParty,
            },
          ] as ColumnsType<any>)
        : []),
      {
        title: "Descripción",
        dataIndex: "descripcion",
        ellipsis: true,
        responsive: ["lg"],
      },
    ],
    [screens.md, screens.lg, isAdmin],
  );

  // Columna "Efecto en cliente": hace explícito si el gasto suma o resta al
  // balance del cliente, y por qué (recibe / paga / no participa). Aplica a
  // todas las categorías que vienen del controller `listExpenses` (aseos,
  // mantenciones/reparaciones, otros) — productos viene de
  // `inventory_transaction` y siempre es egreso puro del cliente, así que
  // ahí no se muestra. Antes el subtotal trataba todo como egreso y el
  // cliente veía en rojo cargos que él RECIBIÓ. Ver flujo-financiero.md §10.5.
  const clienteEffectCol = useMemo<ColumnsType<any>[number]>(
    () => ({
      title: "Efecto en cliente",
      key: "efectoCliente",
      width: 170,
      align: "right",
      render: (_: any, r: any) => {
        const monto = Number(r?.monto) || 0;
        const recibe = r?.pagado_a?.tipo === 1;
        const paga = r?.pagado_por?.tipo === 1;
        if (recibe && !paga) {
          return (
            <span style={{ color: "#389e0d", fontWeight: 600 }}>
              +{money(monto)}
            </span>
          );
        }
        if (paga && !recibe) {
          return (
            <span style={{ color: "#cf1322", fontWeight: 600 }}>
              −{money(monto)}
            </span>
          );
        }
        // Cliente no participa (intermediario AF↔aseador, etc.) o ambos lados
        // son el cliente (autotransacción, raro pero explícito).
        return <span style={{ color: "#8c8c8c" }}>—</span>;
      },
    }),
    [],
  );

  // Cols de "Otros" y de "Reparaciones": expenseCols + columna efecto cliente.
  // Se reutiliza la misma `clienteEffectCol` para mantener una semántica
  // visual consistente entre las pestañas que pueden tener flujos cruzados.
  // "Otros" = TODOS los flujos manuales de la propiedad (toda categoría).
  //
  // "Efecto en cliente" es solo para el ADMIN (round 2026-07-28). En el panel
  // del cliente esa columna nació para distinguir los flujos que lo tocaban de
  // los que no; ahora el backend ya no le manda los que no lo tocan, así que
  // repetiría el monto en todas las filas. Para el admin sigue siendo útil:
  // él sí ve el circuito completo.
  //
  // Y la columna "Categoría" tampoco es para el cliente: en "Otros" caen flujos
  // de toda categoría y a él no le aporta (pedido del dueño, 2026-10-13). Las
  // demás pestañas conservan su Categoría; el admin la sigue viendo siempre.
  const otrosCols = useMemo(
    () =>
      isAdmin
        ? [...expenseCols, clienteEffectCol]
        : expenseCols.filter((c) => "dataIndex" in c && c.dataIndex !== "categoria"),
    [expenseCols, clienteEffectCol, isAdmin],
  );

  // Aseos = aseos de CALENDARIO (tabla `aseos`/Cleaning), NO flujos. Columnas
  // propias del aseo: fecha, estado, aseador, quién paga, costo y el efecto
  // en el cliente (egreso solo si lo paga el propietario, tipo=1). Round
  // 2026-06-05. Orden de columnas pensado para el summary firmado:
  //   0 Fecha | 1 Estado | 2 Aseador | 3 Pagado por | 4 Costo | 5 Efecto
  const aseoEstadoColor: Record<string, string> = {
    pendiente: "orange",
    en_proceso: "blue",
    completado: "green",
    pagado: "green",
  };
  const aseosCols: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "fecha_inicio",
        width: 130,
        render: (v: any) => formatDate(v),
        fixed: screens.md ? "left" : undefined,
      },
      {
        // Código de estadía AF del arriendo al que pertenece este aseo — el
        // mismo #N que aparece en la tabla de Arriendos, para que el cliente
        // ligue el costo del aseo con su arriendo. "—" = aseo sin arriendo.
        title: "Estadía",
        dataIndex: "reserva_id",
        width: 120,
        render: (v: any) =>
          v != null ? <EstadiaCodeTag id={v} withTooltip={false} /> : "—",
      },
      {
        title: "Estado",
        dataIndex: "estado",
        width: 120,
        render: (v: string) =>
          v ? <Tag color={aseoEstadoColor[v] || "default"}>{v}</Tag> : "—",
      },
      {
        title: "Aseador",
        dataIndex: "aseador",
        width: 160,
        ellipsis: true,
        responsive: ["md"],
        render: (v: string) => v || "—",
      },
      // Igual que en "Otros": "Pagado por" y "Efecto en cliente" son columnas
      // de administración. Desde el round 2026-07-28 el cliente solo recibe
      // los aseos que SE LE COBRAN, así que "Pagado por" diría siempre él
      // mismo y "Efecto en cliente" repetiría el costo con signo. El admin
      // sigue viendo todos los aseos de la propiedad y necesita ambas.
      ...(isAdmin
        ? ([
            {
              title: "Pagado por",
              dataIndex: "pagado_por",
              width: 160,
              ellipsis: true,
              responsive: ["md"],
              render: renderParty,
            },
          ] as ColumnsType<any>)
        : []),
      {
        title: "Costo",
        dataIndex: "costo_limpieza",
        width: 150,
        align: "right",
        render: (v: number) => money(Number(v) || 0),
      },
      ...(isAdmin
        ? ([
            {
              title: "Efecto en cliente",
              key: "efectoCliente",
              width: 160,
              align: "right",
              render: (_: any, r: any) => {
                const ef = Number(r?.efectoCliente) || 0;
                if (ef < 0) {
                  return (
                    <span style={{ color: "#cf1322", fontWeight: 600 }}>
                      −{money(Math.abs(ef))}
                    </span>
                  );
                }
                // No lo paga el cliente (AF o arrendatario): no afecta su bolsillo.
                return <span style={{ color: "#8c8c8c" }}>—</span>;
              },
            },
          ] as ColumnsType<any>)
        : []),
    ],
    [screens.md, isAdmin],
  );

  // Columnas de la pestaña Lavandería del cliente (AC7). Todos estos lavados
  // los paga el cliente (el backend ya filtra a pagado_por.tipo=1 + aledaños),
  // por eso "Efecto en cliente" siempre es un egreso.
  const lavadosCols: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "fecha",
        width: 130,
        render: (v) => formatDate(v),
        fixed: screens.md ? "left" : undefined,
      },
      {
        title: "Detalle",
        dataIndex: "descripcion",
        ellipsis: true,
        render: (v: string) => v || "Lavado",
      },
      {
        title: "Lavados",
        dataIndex: "cantidad_lavados",
        width: 90,
        align: "right",
        responsive: ["md"],
        render: (v: number) => Number(v) || 0,
      },
      {
        title: "Secados",
        dataIndex: "cantidad_secados",
        width: 90,
        align: "right",
        responsive: ["md"],
        render: (v: number) => Number(v) || 0,
      },
      {
        title: "Monto",
        dataIndex: "monto",
        width: 150,
        align: "right",
        render: (v: number) => money(Number(v) || 0),
      },
      {
        title: "Efecto en cliente",
        key: "efectoCliente",
        width: 160,
        align: "right",
        render: (_: any, r: any) => {
          const ef = Number(r?.efectoCliente) || 0;
          if (ef < 0) {
            return (
              <span style={{ color: "#cf1322", fontWeight: 600 }}>
                −{money(Math.abs(ef))}
              </span>
            );
          }
          return <span style={{ color: "#8c8c8c" }}>—</span>;
        },
      },
    ],
    [screens.md],
  );

  const productosCols: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "fecha",
        width: 150,
        render: (v) => formatDate(v),
        fixed: screens.md ? "left" : undefined,
      },
      { title: "Producto", dataIndex: "nombre", width: 220 },
      {
        title: "Packs",
        dataIndex: "cantidadDePacks",
        width: 120,
        responsive: ["sm"],
      },
      {
        title: "Unidades",
        dataIndex: "cantidadDeUnidades",
        width: 120,
        responsive: ["md"],
      },
      {
        title: "Monto (total)",
        dataIndex: "monto",
        width: 160,
        align: "right",
        render: (v: number) => money(v),
      },
    ],
    [screens.md],
  );

  const LS_VIEW_KEY = "af_arriendos_view_v1";
  const LS_COLS_KEY = "af_arriendos_cols_v1";

  type ViewMode = "resumida" | "detallada";

  const ADMIN_ALL_COLS: { key: string; label: string; help?: string }[] = [
    {
      key: "id",
      label: ESTADIA_LABEL,
      help: ESTADIA_TOOLTIP,
    },
    {
      key: "nombrePropiedad",
      label: "Propiedad",
      help: "Nombre de la propiedad asociada al arriendo.",
    },
    {
      key: "fechaCreacion",
      label: "Fecha creación",
      help: "Fecha de creación del registro en el sistema.",
    },
    {
      key: "idPropiedadL",
      label: "ID propiedad largo",
      help: "Identificador largo de la propiedad.",
    },
    {
      key: "idReservaV",
      label: "ID reserva largo",
      help: "Identificador largo de la reserva.",
    },
    {
      key: "idReservaC",
      label: "ID reserva corto",
      help: "Identificador corto de la reserva.",
    },
    {
      key: "idReservaPlataforma",
      label: "ID reserva plataforma",
      help: "ID de la reserva en la plataforma (Airbnb, Booking, etc).",
    },
    {
      key: "nombreHuesped",
      label: "Nombre huésped",
      help: "Nombre del huésped principal de la reserva.",
    },
    {
      key: "telefonoHuesped",
      label: "Teléfono huésped",
      help: "Teléfono de contacto del huésped.",
    },
    {
      key: "huespedes",
      label: "N° huéspedes",
      help: "Cantidad de huéspedes en la reserva.",
    },
    {
      key: "ingreso",
      label: "Entrada",
      help: "Fecha de entrada.",
    },
    { key: "salida", label: "Salida", help: "Fecha de salida (YYYY-MM-DD)." },
    {
      key: "noches",
      label: "Noches",
      help: "Cantidad de noches de la reserva.",
    },
    {
      key: "idPlataforma",
      label: "ID plataforma",
      help: "Identificador interno de la plataforma.",
    },
    {
      key: "plataforma",
      label: "Plataforma",
      help: "Nombre de la plataforma (Airbnb, Booking, etc).",
    },
    {
      key: "moneda",
      label: "Moneda",
      help: "Moneda de la reserva (CLP, USD, etc).",
    },
    {
      key: "cambioUsdClp",
      label: "Cambio USD/CLP",
      help: "Tipo de cambio aplicado de USD a CLP.",
    },
    {
      key: "reservaTotalClp",
      label: "Reserva total (CLP)",
      help: "Todo lo que pagó el huésped. No es tu ingreso — es el total. Incluye noches, limpieza y extras. De acá se descuentan comisiones AF + IVA y la limpieza completa antes de llegar a lo que te queda a ti.",
    },
    {
      key: "reservaTotalUsd",
      label: "Reserva total (USD)",
      help: "Monto total de la reserva en dólares.",
    },
    {
      key: "precioNochesClp",
      label: "Precio noches (CLP)",
      help: "Monto total cobrado por noches en CLP.",
    },
    {
      key: "precioNochesUsd",
      label: "Precio noches (USD)",
      help: "Monto total cobrado por noches en USD.",
    },
    {
      key: "limpiezaClp",
      label: "Limpieza (CLP)",
      help: "Tarifa total de limpieza que pagó el huésped. Se descuenta ENTERA de tu ingreso — se redistribuye entre aseador, transporte, materiales y lavandería. No te queda nada de la limpieza.",
    },
    {
      key: "limpiezaUsd",
      label: "Limpieza (USD)",
      help: "Tarifa de limpieza cobrada al huésped en USD.",
    },
    {
      key: "comisionPlataformaClp",
      label: "Comisión plataforma (CLP)",
      help: "Lo que se quedó Airbnb/Booking/Despegar por gestionar la reserva. Ya incluye IVA — no se le suma 19% adicional. Si es reserva directa, suele ser 0.",
    },
    {
      key: "comisionPlataformaUsd",
      label: "Comisión plataforma (USD)",
      help: "Comisión cobrada por la plataforma en USD.",
    },
    {
      key: "extrasComisionClp",
      label: "Extras con comisión (CLP)",
      help: "Extras que el huésped pagó y SÍ generan comisión para Aloja Fácil (misma tasa que noches). Ejemplos: tarifa por mascota, personas adicionales, check-in temprano. Te queda la contraparte (1 − tasa × 1.19).",
    },
    {
      key: "extrasComisionUsd",
      label: "Extras con comisión (USD)",
      help: "Extras sujetos a comisión de AF en USD.",
    },
    {
      key: "extrasSinComisionClp",
      label: "Extras sin comisión (CLP)",
      help: "Extras que el huésped pagó y te quedan 100% a ti. Aloja Fácil NO comisiona sobre estos. Ejemplos: uso de parrilla, cargos del edificio, multas del condominio.",
    },
    {
      key: "extrasSinComisionUsd",
      label: "Extras sin comisión (USD)",
      help: "Extras no sujetos a comisión de AF en USD.",
    },
    {
      key: "impuestoPlataformaClp",
      label: "Impuesto plataforma (CLP)",
      help: "Impuestos cobrados por la plataforma en CLP.",
    },
    {
      key: "impuestoPlataformaUsd",
      label: "Impuesto plataforma (USD)",
      help: "Impuestos cobrados por la plataforma en USD.",
    },
    {
      key: "descuentosClp",
      label: "Descuentos (CLP)",
      help: "Descuentos aplicados a la reserva. Tipo 'ajuste al total': baja la reserva del huésped y la pérdida se reparte proporcional entre Aloja Fácil y tú. Tipo 'solo Aloja Fácil': pérdida exclusiva de Aloja Fácil, tu ingreso no cambia.",
    },
    {
      key: "descuentosUsd",
      label: "Descuentos (USD)",
      help: "Descuentos aplicados a la reserva en USD.",
    },
    {
      key: "tasaComisionAf",
      label: "Tasa comisión AF",
      help: "Porcentaje de comisión de AF sobre la reserva.",
    },
    {
      key: "comisionAfNochesClp",
      label: "AF noches (CLP)",
      help: "Lo que cobra Aloja Fácil por administrar la reserva. Fórmula: precio noches efectivo × tasa de comisión. Se descuenta de tu ingreso junto con el IVA de esta comisión.",
    },
    {
      key: "comisionAfExtrasClp",
      label: "AF extras (CLP)",
      help: "Comisión Aloja Fácil sobre los extras con comisión (SÓLO esos, no los sin comisión). Fórmula: sum(extras con comisión) × tasa.",
    },
    {
      key: "comisionAfNochesUsd",
      label: "AF noches (USD)",
      help: "Comisión AF sobre noches en USD.",
    },
    {
      key: "comisionAfExtrasUsd",
      label: "AF extras (USD)",
      help: "Comisión AF sobre extras en USD.",
    },
    {
      key: "ivaComisionDeNochesClp",
      label: "IVA comisión noches (CLP)",
      help: "IVA 19% sobre la comisión AF noches. Se te factura junto con la comisión. Aloja Fácil lo declara al SII.",
    },
    {
      key: "ivaComisionDeExtrasClp",
      label: "IVA comisión extras (CLP)",
      help: "IVA sobre la comisión de extras de AF en CLP.",
    },
    {
      key: "ivaComisionDeNochesUsd",
      label: "IVA comisión noches (USD)",
      help: "IVA sobre la comisión de noches de AF en USD.",
    },
    {
      key: "totalExtrasClp",
      label: "Total extras (CLP)",
      help: "Suma total de extras en CLP. El cálculo es (extras con comisión + extras sin comisión - descuentos).",
    },
    {
      key: "totalExtrasUsd",
      label: "Total extras (USD)",
      help: "Suma total de extras en USD. El cálculo es (extras con comisión + extras sin comisión - descuentos).",
    },
    {
      key: "ivaComisionDeExtrasUsd",
      label: "IVA comisión extras (USD)",
      help: "IVA sobre la comisión de extras de AF en USD.",
    },
    {
      key: "totalPropietario",
      label: "Reserva total propietario",
      help: "Monto final liquidado al propietario. El cálculo es (reserva total - limpieza - comisiones - IVA + extras - descuentos).",
    },
    {
      key: "estadoPago",
      label: "Estado pago",
      help: "Estado del pago de la reserva (pendiente, pagado, etc).",
    },
    {
      key: "tipoPago",
      label: "Método de pago",
      help: "Método de pago utilizado.",
    },
    {
      key: "action",
      label: "Acción",
      help: "Última acción realizada en el registro.",
    },
    {
      key: "tipo",
      label: "Tipo",
      help: "Tipo de registro (reservador o por reservar).",
    },
    {
      key: "inmutable",
      label: "Inmutable",
      help: "Indica si el registro es inmutable (no editable).",
    },
    {
      key: "notaInterna",
      label: "Nota interna",
      help: "Notas internas para uso administrativo.",
    },
  ];

  const ALL_COLS: { key: string; label: string }[] = [
    { key: "id", label: ESTADIA_LABEL },
    { key: "ingreso", label: "Entrada" },
    { key: "salida", label: "Salida" },
    { key: "noches", label: "Noches" },
    { key: "plataforma", label: "Plataforma" },
    { key: "nombreHuesped", label: "Huésped" },
    { key: "huespedes", label: "Huéspedes" },
    { key: "precioNoches", label: "Precio noches (CLP)" },
    { key: "limpieza", label: "Limpieza (CLP)" },
    { key: "comisionPlataforma", label: "Comisión plataforma" },
    { key: "extrasConGroup", label: "Extras con Comisión" },
    { key: "extrasSinGroup", label: "Extras sin Comisión" },
    { key: "descuentosGroup", label: "Descuentos Monto" },
    { key: "comisionAfNoches", label: "Comisión Aloja Fácil Noches" },
    { key: "comisionAfExtras", label: "Comisión Aloja Fácil Extras" },
    { key: "ivaAfNoches", label: "IVA comisión Aloja Fácil Noches" },
    { key: "ivaAfExtras", label: "IVA comisión Aloja Fácil Extras" },
    { key: "totalPropietario", label: "Ingresos" },
  ];

  // Default de columnas para la vista "Resumida" del cliente.
  //
  // Antes solo mostraba ingreso/salida/plataforma/precioNoches/totalPropietario.
  // Eso ocultaba al propietario los descuentos sobre la reserva (comisión AF
  // y su IVA), aunque sí se le restaban en `Tu ingreso`. El usuario pidió
  // mostrarlos siempre — el IVA cuenta como ingreso para AF y por lo tanto
  // como egreso indirecto del propietario, así que pertenece a la lista
  // mínima para que el propietario entienda la cifra final.
  // Ver docs/flujo-financiero.md §5 (fórmula totalPropietario) y §8 (IVA).
  const RESUMIDA_CLIENTE = [
    "id",
    "ingreso",
    "salida",
    "plataforma",
    "precioNoches",
    "comisionAfNoches",
    "comisionAfExtras",
    "ivaAfNoches",
    "ivaAfExtras",
    "totalPropietario",
  ];

  function H(key: string, title?: string) {
    const rawTitle = title || key;

    // Transformar a multilinea
    const formattedTitle = (
      <>
        {rawTitle.split(" ").map((word, i) => (
          <React.Fragment key={i}>
            {word}
            <br />
          </React.Fragment>
        ))}
      </>
    );

    return <HeaderWithInfo title={formattedTitle} help={getAdminHelp(key)} />;
  }

  const buildColumnForKey = (key: string): any => {
    const col: any = {
      key,
      dataIndex: key,
      title: H(key, getAdminLabel(key)),
      width: 160,
      align: "center" as const,
    };

    if (DATE_KEYS.has(key)) {
      col.render = (v: any) => (v ? dayjs(v).format("DD-MM-YYYY") : "—");
      col.width = 140;
      return col;
    }

    if (CLP_KEYS.has(key)) {
      col.width = 170;
      col.render = (v: number) => (
        <MoneyChip
          value={Number(v) || 0}
          kind={moneyKindFor(key)}
          currency="CLP"
          bold={isOwnerTotal(key)}
        />
      );
      return col;
    }

    if (USD_KEYS.has(key)) {
      col.width = 180;
      col.render = (v: number) => (
        <MoneyChip
          value={Number(v) || 0}
          kind={moneyKindFor(key)}
          currency="USD"
          bold={isOwnerTotal(key)}
        />
      );
      return col;
    }

    if (/^(noches|numeroHuespedes|cambioUsdClp|tasaComisionAf)$/.test(key)) {
      col.width = 120;
      return col;
    }

    if (key === "id") {
      // Código de estadía AF (booking.id). Título compacto (2 líneas) con el
      // tooltip explicativo; celdas con el Tag «Estadía #N».
      col.width = 150;
      col.title = (
        <HeaderWithInfo
          title={
            <>
              Código
              <br />
              estadía
            </>
          }
          help={ESTADIA_TOOLTIP}
        />
      );
      col.render = (v: any) => <EstadiaCodeTag id={v} withTooltip={false} />;
      col.sorter = (a: any, b: any) => Number(a.id ?? 0) - Number(b.id ?? 0);
      return col;
    }
    if (key.startsWith("id_")) {
      col.width = 120;
      return col;
    }
    if (key === "nombrePropiedad") {
      // Solo presentación: el nombre de propiedad se muestra en MAYÚSCULAS.
      // No afecta búsqueda/orden/exportación (el dato crudo queda intacto).
      col.width = 200;
      col.render = (v: any) => formatPropertyName(v) || "—";
      return col;
    }
    if (key.includes("nombre")) {
      col.width = 200;
      return col;
    }
    if (key.includes("telefono")) {
      col.width = 180;
      col.render = (v: any) => formatPhoneDisplay(v) || "—";
      return col;
    }
    if (key === "notaInterna") {
      col.width = 260;
      return col;
    }

    return col;
  };

  const getAdminLabel = (k: string) =>
    ADMIN_ALL_COLS.find((c) => c.key === k)?.label ?? pretty(k);

  const getAdminHelp = (k: string) =>
    ADMIN_ALL_COLS.find((c) => c.key === k)?.help;

  const buildAdminSummaryColumns = (keys: string[]): ColumnsType<any> =>
    keys.flatMap((k) => {
      if (
        k === "extrasComisionClp" ||
        k === "extrasSinComisionClp" ||
        k === "descuentosClp" ||
        k === "extrasComisionUsd" ||
        k === "extrasSinComisionUsd" ||
        k === "descuentosUsd"
      ) {
        const listKey = k;
        const kind = k.startsWith("descuentos") ? "out" : "in";

        return [
          {
            key: `${k}_group`,
            title: H(k, getAdminLabel(k)),
            children: [
              {
                key: `${k}_razon`,
                title: <CompactTitle title="Razón" />,
                align: "center" as const,
                width: 160,
                render: (_: any, r: any) => (
                  <ListCol items={r[listKey]} field="razon" kind={kind} />
                ),
              },
              {
                key: `${k}_monto`,
                title: <CompactTitle title="Monto" />,
                align: "center" as const,
                width: 120,
                render: (_: any, r: any) => (
                  <ListCol items={r[listKey]} field="monto" kind={kind} />
                ),
              },
            ],
          },
        ];
      }

      const base = buildColumnForKey(k);
      return [
        {
          ...base,
          title: H(k, getAdminLabel(k)),
          width: base.width ? Math.min(Number(base.width) || 140, 140) : 140,
        },
      ];
    });

  const adminDetalladaCols: ColumnsType<any> = useMemo(() => {
    const cols: any[] = [];
    ADMIN_DETAILED_KEYS.forEach((key) => {
      if (isJsonbListKey(key)) {
        cols.push(
          buildJsonbGroup({
            key: `${key}_group`,
            title: (
              <HeaderWithInfo
                title={<CompactTitle title={getAdminLabel(key)} />}
                help={getAdminHelp(key)}
              />
            ),
            listKey: key,
            kind: moneyKindFor(key) === "out" ? "out" : "in",
          }),
        );
      } else {
        cols.push(buildColumnForKey(key));
      }
    });
    return cols;
  }, []);

  const adminResumidaCols = useMemo(
    () => buildAdminSummaryColumns(ADMIN_SUMMARY_KEYS),
    [],
  );

  /**
   * Columnas visibles de fábrica en cada vista, POR ROL.
   *
   * ── El bug que cierra (round 2026-08-21) ──────────────────────────────────
   *
   * Esto era `ALL_COLS.map(...)` a secas, y `ALL_COLS` es la lista del
   * CLIENTE. Para el admin, `adminDetalladaColsFiltered` filtra sus 51 columnas
   * detalladas contra esa lista, y las claves no son las mismas: el cliente
   * tiene `precioNoches`, `limpieza`, `comisionPlataforma`; el admin tiene
   * `precioNochesClp`, `limpiezaClp`, `comisionPlataformaClp`. Solo coincidían
   * 8 nombres por casualidad — los que no llevan sufijo de moneda.
   *
   * Resultado medido: en admin, «Resumida» mostraba 22 columnas y «Detallada»
   * mostraba 8. Los dos botones funcionaban perfecto; lo que estaba dado vuelta
   * era el CONTENIDO. Un round anterior miró el control, no encontró nada
   * invertido y reforzó el resaltado por CSS — el control nunca fue el problema.
   *
   * Toda la selección de columnas del admin ya se hace contra `ADMIN_ALL_COLS`
   * (el selector de columnas, `allKeys`); esta era la única que se había
   * quedado con la lista del cliente.
   */
  const DETALLADA_DEFAULT = useMemo(
    () => (isAdmin ? ADMIN_ALL_COLS : ALL_COLS).map((c) => c.key),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin],
  );

  const loadLS = <T,>(k: string, fallback: T): T => {
    try {
      const v = localStorage.getItem(k);
      return v ? (JSON.parse(v) as T) : fallback;
    } catch {
      return fallback;
    }
  };
  const saveLS = (k: string, v: any) => {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {}
  };

  const isMobile = !screens.md;

  // La resumida del admin no filtra por `visibleCols` (usa `adminResumidaCols`
  // entero), pero este valor igual se persiste y se hereda al cambiar de vista:
  // dejarlo con claves de cliente reintroduce el mismo desajuste por otro lado.
  const RESUMIDA_DEFAULT = useMemo(
    () => (isAdmin ? ADMIN_SUMMARY_KEYS : RESUMIDA_CLIENTE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin],
  );

  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    loadLS<ViewMode>(LS_VIEW_KEY, "resumida"),
  );
  const [visibleCols, setVisibleCols] = useState<string[]>(() =>
    loadLS<string[]>(
      LS_COLS_KEY,
      viewMode === "resumida" ? RESUMIDA_DEFAULT : DETALLADA_DEFAULT,
    ),
  );

  /**
   * Default de vista SEGÚN EL ROL, pero solo si nadie eligió todavía.
   *
   * Antes esto era `setViewMode(isAdmin ? "detallada" : "resumida")` a secas, y
   * pisaba la elección guardada en cada carga: el modo se persistía en
   * localStorage y se descartaba dos líneas después. Peor, `isAdmin` sale del
   * usuario del contexto, que llega asíncrono: el efecto vuelve a correr cuando
   * resuelve y le cambia la vista al usuario A MITAD de sesión, sin que haya
   * tocado nada.
   *
   * Con el guard, el rol decide el arranque la primera vez y después manda la
   * persona.
   */
  useEffect(() => {
    // Si la persona ya eligió, su elección manda. `LS_VIEW_KEY` solo se escribe
    // desde `cambiarVista`, o sea desde un clic real.
    if (localStorage.getItem(LS_VIEW_KEY)) return;
    setViewMode(isAdmin ? "detallada" : "resumida");
  }, [isAdmin]);

  /**
   * Cambiar de vista. La persistencia vive ACÁ y no en un efecto sobre
   * `viewMode`: el efecto corría también al montar, así que la preferencia
   * quedaba escrita antes de que nadie tocara nada y no había forma de
   * distinguir "el usuario eligió esto" de "es el default". El resultado era
   * que el default por rol nunca podía aplicarse... y, a la vez, que la
   * elección guardada se pisaba en cada carga.
   */
  const cambiarVista = (v: ViewMode) => {
    setViewMode(v);
    saveLS(LS_VIEW_KEY, v);
  };

  // `isAdmin` sale del usuario del contexto y llega ASÍNCRONO. Sin él en las
  // dependencias, un admin con la vista ya guardada en localStorage nunca
  // reejecutaba esto —`viewMode` no cambiaba— y se quedaba con los defaults
  // calculados mientras el rol todavía era desconocido, o sea los del cliente.
  // Es la misma trampa que ya había mordido al default de vista.
  useEffect(() => {
    if (viewMode === "resumida") {
      setVisibleCols(RESUMIDA_DEFAULT);
      saveLS(LS_COLS_KEY, RESUMIDA_DEFAULT);
    } else {
      setVisibleCols(DETALLADA_DEFAULT);
      saveLS(LS_COLS_KEY, DETALLADA_DEFAULT);
    }
  }, [viewMode, isAdmin, RESUMIDA_DEFAULT, DETALLADA_DEFAULT]);

  const onChangeCols = (vals: any[]) => {
    const v = vals.map(String);
    setVisibleCols(v);
    saveLS(LS_COLS_KEY, v);
  };

  const baseCols = useMemo<ColumnsType<ArriendoRow>>(
    () => [
      {
        // Código de estadía AF (booking.id) — visible para el cliente para que
        // identifique el arriendo al reclamar. Primera columna, fija a la izq.
        key: "id",
        dataIndex: "id",
        align: "center",
        title: (
          <HeaderWithInfo
            title={
              <>
                Código
                <br />
                estadía
              </>
            }
            help={ESTADIA_TOOLTIP}
          />
        ),
        width: 130,
        render: (v: any) => <EstadiaCodeTag id={v} withTooltip={false} />,
        sorter: (a: ArriendoRow, b: ArriendoRow) =>
          Number(a.id ?? 0) - Number(b.id ?? 0),
        fixed: "left",
      },
      {
        key: "ingreso",
        dataIndex: "ingreso",
        align: "center",
        title: (
          <HeaderWithInfo
            title="Entrada"
            help="Fecha de ingreso (YYYY-MM-DD)"
          />
        ),
        width: 120,
        render: (v: string | null) => formatDate(v),
        fixed: "left",
      },
      {
        key: "salida",
        dataIndex: "salida",
        align: "center",
        title: (
          <HeaderWithInfo title="Salida" help="Fecha de salida (YYYY-MM-DD)" />
        ),
        width: 120,
        render: (v: string | null) => formatDate(v),
        fixed: "left",
      },
      {
        key: "noches",
        dataIndex: "noches",
        title: <HeaderWithInfo title="Noches" help="Noches de estadía" />,
        width: 90,
        align: "center",
      },
      {
        key: "plataforma",
        dataIndex: "plataforma",
        align: "center",
        title: (
          <HeaderWithInfo title="Plataforma" help="Airbnb, Booking, etc." />
        ),
        width: 140,
      },
      {
        key: "nombreHuesped",
        dataIndex: "nombreHuesped",
        align: "center",
        title: <HeaderWithInfo title="Huésped" help="Titular de la reserva" />,
        width: 200,
        ellipsis: true,
      },
      {
        key: "huespedes",
        dataIndex: "huespedes",
        title: <HeaderWithInfo title="Huéspedes" help="Cantidad de personas" />,
        width: 110,
        align: "center",
      },
      {
        key: "precioNoches",
        dataIndex: "precioNochesClp",
        title: (
          <HeaderWithInfo
            title="Monto total de las noches"
            help="Suma del precio por noche de la reserva"
          />
        ),
        width: 170,
        align: "center",
        render: (v) => <MoneyChip value={v} kind="in" />,
      },
      {
        key: "limpieza",
        dataIndex: "limpiezaClp",
        title: (
          <HeaderWithInfo
            title="Limpieza (CLP)"
            help="Tarifa limpieza cobrada al huésped"
          />
        ),
        width: 150,
        align: "right",
        render: (v) => <MoneyChip value={v} kind="cleaning" />,
      },
      {
        key: "comisionPlataforma",
        dataIndex: "comisionPlataformaClp",
        title: (
          <HeaderWithInfo
            title="Comisión plataforma"
            help="Retención plataforma"
          />
        ),
        width: 180,
        align: "right",
        render: (v) => <MoneyChip value={v} kind="commission" />,
      },
      {
        key: "extrasConGroup",
        title: (
          <HeaderWithInfo
            title="Extras con comisión"
            help="Extras con comisión a Aloja Fácil"
          />
        ),
        children: [
          {
            key: "extrasConRazon",
            dataIndex: "extrasComisionClp",
            title: "Razón",
            align: "center",
            width: 200,
            render: (_: any, r) => (
              <ListCol
                items={r.extrasComisionClp ?? []}
                field="razon"
                kind="in"
              />
            ),
          },
          {
            key: "extrasConMonto",
            dataIndex: "extrasComisionClp",
            title: "Monto",
            width: 150,
            align: "center",
            render: (_: any, r) => (
              <ListCol
                items={r.extrasComisionClp ?? []}
                field="monto"
                kind="in"
              />
            ),
          },
        ],
      },
      {
        key: "extrasSinGroup",
        title: (
          <HeaderWithInfo
            title="Extras sin comisión"
            help="Extras sin comisión a Aloja Fácil"
          />
        ),
        children: [
          {
            key: "extrasSinRazon",
            dataIndex: "extrasSinComisionClp",
            title: "Razón",
            align: "center",
            width: 200,
            render: (_: any, r) => (
              <ListCol
                items={r.extrasSinComisionClp ?? []}
                field="razon"
                kind="in"
              />
            ),
          },
          {
            key: "extrasSinMonto",
            dataIndex: "extrasSinComisionClp",
            title: "Monto",
            width: 150,
            align: "center",
            render: (_: any, r) => (
              <ListCol
                items={r.extrasSinComisionClp ?? []}
                field="monto"
                kind="in"
              />
            ),
          },
        ],
      },
      {
        key: "descuentosGroup",
        title: (
          <HeaderWithInfo
            title="Descuentos"
            help="Descuentos aplicados a la reserva"
          />
        ),
        children: [
          {
            key: "descuentosRazon",
            dataIndex: "descuentosClp",
            title: "Razón",
            align: "center",
            width: 200,
            render: (_: any, r) => (
              <ListCol items={r.descuentosClp ?? []} field="razon" kind="out" />
            ),
          },
          {
            key: "descuentosMonto",
            dataIndex: "descuentosClp",
            title: "Monto",
            width: 150,
            align: "center",
            render: (_: any, r) => (
              <ListCol items={r.descuentosClp ?? []} field="monto" kind="out" />
            ),
          },
        ],
      },
      {
        key: "comisionAfNoches",
        dataIndex: "comisionAfNochesClp",
        title: (
          <HeaderWithInfo
            title="Comisión Aloja Fácil Noches"
            help="Comisión de Aloja Fácil sobre el precio de las noches."
          />
        ),
        width: 140,
        align: "right",
        render: (v) => <MoneyChip value={v} kind="out" />,
      },
      {
        key: "comisionAfExtras",
        dataIndex: "comisionAfExtrasClp",
        title: (
          <HeaderWithInfo
            title="Comisión Aloja Fácil Extras"
            help="Comisión de Aloja Fácil sobre los extras cobrados al huésped."
          />
        ),
        width: 140,
        align: "right",
        render: (v) => <MoneyChip value={v} kind="out" />,
      },
      {
        key: "ivaAfNoches",
        // El backend persiste el IVA en `iva_comision_de_noches_clp`
        // (ver booking.model.js + docs/flujo-financiero.md §7 tabla
        // "Mapping snake_case ↔ camelCase"), y el mapper de
        // useArriendosSection.ts lo expone como `ivaComisionDeNochesClp`.
        // Antes este dataIndex apuntaba a `ivaAfNochesClp` (campo
        // inexistente) y la columna mostraba siempre $0 al cliente
        // aunque el IVA sí se descontaba en `Tu ingreso`.
        dataIndex: "ivaComisionDeNochesClp",
        title: (
          <HeaderWithInfo
            title="IVA comisión Aloja Fácil Noches"
            help="IVA sobre la comisión de Aloja Fácil por las noches."
          />
        ),
        width: 150,
        align: "right",
        render: (v) => <MoneyChip value={v} kind="out" />,
      },
      {
        key: "ivaAfExtras",
        // Idem: el campo correcto del mapper es `ivaComisionDeExtrasClp`.
        dataIndex: "ivaComisionDeExtrasClp",
        title: (
          <HeaderWithInfo
            title="IVA comisión Aloja Fácil Extras"
            help="IVA sobre la comisión de Aloja Fácil por los extras."
          />
        ),
        width: 150,
        align: "right",
        render: (v) => <MoneyChip value={v} kind="out" />,
      },
      {
        key: "totalPropietario",
        dataIndex: "totalPropietario",
        title: (
          <HeaderWithInfo
            title="Tu ingreso"
            help={`Lo que te queda a ti (propietario/cliente) por este arriendo.

Fórmula: Reserva total
− Comisión de la plataforma (Airbnb/Booking/etc., ya con IVA)
− Comisión de Aloja Fácil sobre las noches + IVA
− Comisión de Aloja Fácil sobre los extras con comisión + IVA
− Limpieza entera (se redistribuye entre aseador, transporte, materiales y lavandería)

Los extras sin comisión van 100% a ti.
Los descuentos "solo Aloja Fácil" no afectan tu ingreso.

Ver docs/flujo-financiero.md §5.`}
          />
        ),
        width: 200,
        align: "right",
        fixed: "right",
        render: (v) => <MoneyChip value={v} kind="in" bold />,
      },
    ],
    [],
  );

  const columns = useMemo<ColumnsType<any>>(() => {
    if (isAdmin) {
      // admin ve todas
      return viewMode === "detallada" ? adminDetalladaCols : adminResumidaCols;
    } else {
      const allowed = new Set(visibleCols);
      return baseCols.filter((c) => allowed.has(String(c.key)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAdmin,
    viewMode,
    adminDetalladaCols,
    adminResumidaCols,
    JSON.stringify(visibleCols),
    screens.lg,
  ]);

  // Filas visibles. El backend ya filtra server-side los arriendos según
  // el rol; el frontend solo pasa los rows tal cual.
  const arriendosVisibles = useMemo(
    () => arr.rows ?? [],
    [arr.rows],
  );

  const totalArriendos = useMemo(
    () =>
      arriendosVisibles.reduce(
        (acc: number, r: any) =>
          acc + (r.totalPropietario ?? r.reserva_total_propietario ?? 0),
        0,
      ),
    [arriendosVisibles],
  );

  const adminDetalladaColsFiltered = useMemo(() => {
    if (!isAdmin || !visibleCols) return adminDetalladaCols;
    // Solo deja las columnas (o grupos) que están en visibleCols
    const filterCols = (cols: ColumnsType<any>): ColumnsType<any> =>
      cols
        .map((col) => {
          if ("children" in col && Array.isArray(col.children)) {
            // Es un grupo, filtra hijos
            const filteredChildren = filterCols(col.children);
            if (filteredChildren.length === 0) return null;
            return { ...col, children: filteredChildren };
          }
          return visibleCols.includes(String(col.key)) ? col : null;
        })
        .filter(Boolean) as ColumnsType<any>;
    return filterCols(adminDetalladaCols);
  }, [adminDetalladaCols, visibleCols, isAdmin]);

  // Elegir columnas según rol + modo
  const columnsToUse: ColumnsType<any> = useMemo(() => {
    if (isAdmin) {
      return viewMode === "detallada"
        ? adminDetalladaColsFiltered
        : adminResumidaCols;
    }
    return columns;
  }, [
    isAdmin,
    viewMode,
    adminDetalladaColsFiltered,
    adminResumidaCols,
    columns,
  ]);

  const allKeys = (isAdmin ? ADMIN_ALL_COLS : ALL_COLS).map((c) => c.key);

  const ColumnChooser = (
    <Popover
      placement="bottomRight"
      trigger="click"
      content={
        <>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              indeterminate={
                visibleCols.length > 0 && visibleCols.length < allKeys.length
              }
              checked={visibleCols.length === allKeys.length}
              onChange={(e) => {
                const checked = e.target.checked;
                const next = checked ? allKeys : [];
                setVisibleCols(next);
                saveLS(LS_COLS_KEY, next);
              }}
            >
              Seleccionar todas
            </Checkbox>
          </div>
          <Checkbox.Group
            value={visibleCols}
            onChange={onChangeCols}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              maxWidth: 360,
            }}
          >
            {(isAdmin ? ADMIN_ALL_COLS : ALL_COLS).map((c) => (
              <Checkbox
                key={c.key}
                value={c.key}
                disabled={
                  c.key === "totalPropietario" && viewMode === "resumida"
                }
              >
                {c.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </>
      }
    >
      <Button icon={<SettingOutlined />}>Columnas</Button>
    </Popover>
  );

  const TopFilters = (
    <Space wrap style={{ marginBottom: 12, alignItems: "center" }}>
      {/* Selector de Propiedad con búsqueda */}
      <Select
        style={{ minWidth: 260 }}
        placeholder="Selecciona una propiedad"
        loading={loadingProps}
        value={selectedPropId ?? undefined}
        onChange={(id) => onChangeProp(id)}
        showSearch
        optionFilterProp="label"
        filterOption={(input, option) =>
          fuzzyIncludes(option?.label, input)
        }
        options={propsSelectOptions}
      />
      <DatePicker
        picker="month"
        value={selectedMonth}
        onChange={(m) => setSelectedMonth(m)}
        allowClear={false}
      />
      <Typography.Link style={{ marginLeft: 8 }} onClick={gotoPrevMonth}>
        Ver mes anterior
      </Typography.Link>
      {/* Resumen mensual oficial en PDF / Excel (round 2026-10-29). El servidor decide si el mes está publicado. */}
      <DescargarResumen
        mes={selectedMonth}
        propiedades={propsList}
        propiedadId={selectedPropId}
        esAdmin={isAdmin}
      />
    </Space>
  );

  /**
   * Summary signed para tablas que vienen de `listExpenses` (aseos /
   * reparaciones / otros). El backend devuelve `totals.ingresoCliente` y
   * `totals.egresoCliente` — desde la óptica del Cliente:
   *  - ingresoCliente = SUM(monto) WHERE pagado_a.tipo === 1
   *  - egresoCliente  = SUM(monto) WHERE pagado_por.tipo === 1
   *  - neto           = ingresoCliente − egresoCliente
   *
   * Si no hay flujo cruzado (`ingresoCliente === 0`), colapsa a una sola
   * fila "Subtotal" como el legacy. Si lo hay, muestra recibe / paga / neto
   * para ser explícito. Mantiene el contrato de columnas de cada tabla
   * (colSpan + cellsAfter) porque AntD requiere cubrir N celdas exactas.
   * Ver flujo-financiero.md §10.5.
   */
  /**
   * Posiciones del subtotal DERIVADAS de las columnas reales.
   *
   * Antes cada pestaña pasaba `leftColSpan` / `montoCellIndex` / `cellsAfter`
   * a mano, y eso se desincroniza sin avisar: la pestaña Aseos declaraba 6
   * columnas en un comentario cuando ya tenía 7 (se agregó "Estadía" y nadie
   * corrigió los números), así que el subtotal quedaba corrido una celda a la
   * izquierda del costo. Calcularlo desde el array de columnas hace que
   * agregar o quitar una —como ahora, según el rol— no pueda romperlo.
   *
   * @param cols columnas que realmente se van a renderizar
   * @param montoKey `key` o `dataIndex` de la columna donde va el total
   */
  const summaryPos = (cols: ColumnsType<any>, montoKey: string) => {
    const idx = cols.findIndex(
      (c: any) => (c?.key ?? c?.dataIndex) === montoKey,
    );
    const i = idx >= 0 ? idx : Math.max(0, cols.length - 1);
    return {
      leftColSpan: Math.max(1, i),
      montoCellIndex: i,
      cellsAfter: Math.max(0, cols.length - i - 1),
    };
  };

  const renderClienteSignedSummary = (opts: {
    totals: { ingresoCliente?: number; egresoCliente?: number; monto?: number } | null;
    label: string;
    leftColSpan: number;
    montoCellIndex: number;
    cellsAfter: number;
  }) => {
    const ing = Number(opts.totals?.ingresoCliente || 0);
    const eg = Number(opts.totals?.egresoCliente || 0);
    const neto = ing - eg;
    // Sin ingreso cruzado para el cliente: colapsa al "Subtotal" tradicional
    // mostrando solo el egreso (mismo aspecto que antes para el caso típico).
    if (ing === 0) {
      return (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={opts.leftColSpan}>
            <b>{opts.label}</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={opts.montoCellIndex} align="right">
            <b style={{ color: eg > 0 ? "#cf1322" : undefined }}>
              {eg > 0 ? `−${money(eg)}` : money(eg)}
            </b>
          </Table.Summary.Cell>
          {opts.cellsAfter > 0 && (
            <Table.Summary.Cell
              index={opts.montoCellIndex + 1}
              colSpan={opts.cellsAfter}
            />
          )}
        </Table.Summary.Row>
      );
    }
    return (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={opts.leftColSpan}>
            Recibe el cliente
          </Table.Summary.Cell>
          <Table.Summary.Cell index={opts.montoCellIndex} align="right">
            <span style={{ color: "#389e0d" }}>+{money(ing)}</span>
          </Table.Summary.Cell>
          {opts.cellsAfter > 0 && (
            <Table.Summary.Cell
              index={opts.montoCellIndex + 1}
              colSpan={opts.cellsAfter}
            />
          )}
        </Table.Summary.Row>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={opts.leftColSpan}>
            Paga el cliente
          </Table.Summary.Cell>
          <Table.Summary.Cell index={opts.montoCellIndex} align="right">
            <span style={{ color: "#cf1322" }}>−{money(eg)}</span>
          </Table.Summary.Cell>
          {opts.cellsAfter > 0 && (
            <Table.Summary.Cell
              index={opts.montoCellIndex + 1}
              colSpan={opts.cellsAfter}
            />
          )}
        </Table.Summary.Row>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={opts.leftColSpan}>
            <b>{opts.label}</b>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={opts.montoCellIndex} align="right">
            <b style={{ color: neto >= 0 ? "#389e0d" : "#cf1322" }}>
              {money(neto)}
            </b>
          </Table.Summary.Cell>
          {opts.cellsAfter > 0 && (
            <Table.Summary.Cell
              index={opts.montoCellIndex + 1}
              colSpan={opts.cellsAfter}
            />
          )}
        </Table.Summary.Row>
      </>
    );
  };

  const commonTableProps = {
    className: "finanzas-table",
    pagination: {} as Record<string, any>,
    size: "middle" as const,
    sticky: true,
    scroll: { x: "max-content" as const, y: 520 as number },
  };

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="historial-page"
    >
      <main className="finanzas-page">
        <div className="finanzas-title">
          <PageHeader
            title="Finanzas"
            helpText={
              <>
                <div>
                  Ingresos, gastos y balance por propiedad y período. Filtra
                  por fecha y propiedad.
                </div>
                <div style={{ marginTop: 6 }}>
                  <b>Importante</b>: el "pago al aseador" de los aseos
                  generados automáticamente con cada reserva NO se descuenta
                  acá — ese costo lo paga el huésped dentro de la reserva
                  (rol {isAdmin ? "AF" : "Aloja Fácil"} intermediario). Sólo aparecen en esta liquidación
                  los gastos donde el cliente realmente paga: aseos manuales,
                  mantenciones, lavandería externa, etc.
                </div>
              </>
            }
          />
        </div>

        {/*
          Banner explícito sobre el cierre de mes (doc flujo-financiero.md
          §13). El Cliente solo ve aquí arriendos ya publicados — los no
          publicados son filtrados por el backend. Dejamos el aviso
          permanente para que sepa por qué algunos arriendos recientes
          pueden no aparecer todavía.
        */}
        <Alert
          type="info"
          showIcon
          banner
          style={{ marginBottom: 12 }}
          message={
            <span>
              La información de cada mes (arriendos, gastos, aseos, productos y
              lavandería) se publica <strong>cuando la administración cierra el
              mes</strong>. Hasta ese cierre, el mes en curso puede aún no
              aparecer — está en revisión. Si necesitas adelantar algún caso
              puntual, escríbenos.
            </span>
          }
        />

        <div className="finanzas-filters">{TopFilters}</div>
        <div className="finanzas-tabs">
          <Tabs
            items={[
              {
                key: "finanzas",
                label: "Finanzas",
                children: (
                  <FinanzasTab
                    ingresos={finanzasTab.ingresos}
                    gastos={finanzasTab.gastos}
                    subtotalIngresos={finanzasTab.subtotalIngresos}
                    subtotalGastos={finanzasTab.subtotalGastos}
                    total={finanzasTab.total}
                    loading={finanzasTab.loading}
                    /* Egresos del cliente que afectan su saldo pero no viven en
                       arriendos/flujos → el Total de la pestaña pasa a ser el
                       saldo completo (= "Total general" del Resumen). Audit
                       completitud 2026-06-21. egresoLavadosCliente cableado en
                       AC7 (2026-06-22): lavados aledaños que paga el cliente. */
                    egresoAseosCliente={Number(aseos.totals?.egresoCliente || 0)}
                    egresoProductos={Number(productos.totals?.monto || 0)}
                    egresoLavadosCliente={Number(lavados.totals?.egresoCliente || 0)}
                  />
                ),
              },
              {
                key: "aseos",
                label: "Aseos",
                children: (
                  <Card bodyStyle={{ paddingTop: 12 }}>
                    <Table
                      {...commonTableProps}
                      rowKey="id"
                      loading={aseos.loading}
                      dataSource={aseos.rows}
                      columns={aseosCols}
                      pagination={{
                        ...commonTableProps.pagination,
                        ...paginacionServidor({
                          page: aseos.page,
                          size: aseos.size,
                          total: aseos.total,
                          etiqueta: "aseo",
                          onChange: aseos.onPaginate,
                        }),
                      }}
                      expandable={{
                        expandedRowRender: (r) => (
                          <div style={{ maxWidth: 900 }}>
                            {r.observaciones || "Sin observaciones"}
                          </div>
                        ),
                        rowExpandable: (r) => !!r.observaciones,
                      }}
                      // El subtotal se alinea con la columna "Costo" sean
                      // cuales sean las columnas visibles (varían por rol).
                      summary={() =>
                        renderClienteSignedSummary({
                          totals: aseos.totals,
                          label: "Subtotal aseos (cliente)",
                          ...summaryPos(aseosCols, "costo_limpieza"),
                        })
                      }
                    />
                  </Card>
                ),
              },
              {
                key: "lavanderia",
                label: "Lavandería",
                children: (
                  <Card bodyStyle={{ paddingTop: 12 }}>
                    {/* Explicación explícita con ejemplos (mandato del owner):
                        el cliente debe entender POR QUÉ ve solo algunos lavados. */}
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Solo los lavados que te cobramos a ti"
                      description={
                        <div style={{ fontSize: 13 }}>
                          Aquí aparecen únicamente los <b>lavados sueltos que pediste tú</b> y
                          que te cobramos — por ejemplo, lavar una cortina, o un lavado cuando
                          usaste tu propiedad sin que hubiera arrendatario.
                          <br />
                          <b>No</b> aparecen los lavados de tus arriendos: su costo{" "}
                          <b>ya está descontado dentro de la limpieza de cada reserva</b>{" "}
                          (lo ves en la pestaña Arriendos), así que mostrarlos de nuevo sería
                          cobrártelos dos veces. Tampoco aparecen los lavados que Aloja Fácil
                          te regaló o asumió por su cuenta (no te afectan).
                        </div>
                      }
                    />
                    <Table
                      {...commonTableProps}
                      rowKey="id"
                      loading={lavados.loading}
                      dataSource={lavados.rows}
                      columns={lavadosCols}
                      pagination={{
                        ...commonTableProps.pagination,
                        ...paginacionServidor({
                          page: lavados.page,
                          size: lavados.size,
                          total: lavados.total,
                          etiqueta: "lavado",
                          onChange: lavados.onPaginate,
                        }),
                      }}
                      summary={() =>
                        renderClienteSignedSummary({
                          totals: lavados.totals,
                          label: "Subtotal lavandería (cliente)",
                          ...summaryPos(lavadosCols, "monto"),
                        })
                      }
                    />
                  </Card>
                ),
              },
              {
                key: "productos",
                label: "Productos",
                children: (
                  <Card bodyStyle={{ paddingTop: 12 }}>
                    <Table
                      {...commonTableProps}
                      rowKey="id"
                      loading={productos.loading}
                      dataSource={productos.rows}
                      columns={productosCols}
                      pagination={{
                        ...commonTableProps.pagination,
                        ...paginacionServidor({
                          page: productos.page,
                          size: productos.size,
                          total: productos.total,
                          etiqueta: "producto",
                          onChange: productos.onPaginate,
                        }),
                      }}
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            <b>Subtotal productos</b>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <b>{money(productos.totals?.monto || 0)}</b>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </Card>
                ),
              },
              {
                key: "otros",
                label: "Otros",
                children: (
                  <Card bodyStyle={{ paddingTop: 12 }}>
                    <Table
                      {...commonTableProps}
                      rowKey="id"
                      loading={otros.loading}
                      dataSource={otros.rows}
                      columns={otrosCols}
                      pagination={{
                        ...commonTableProps.pagination,
                        ...paginacionServidor({
                          page: otros.page,
                          size: otros.size,
                          total: otros.total,
                          etiqueta: ["movimiento", "movimientos"],
                          onChange: otros.onPaginate,
                        }),
                      }}
                      expandable={{
                        expandedRowRender: (r) => (
                          <div style={{ maxWidth: 900 }}>
                            {r.descripcion || "Sin descripción"}
                          </div>
                        ),
                        rowExpandable: (r) => !!r.descripcion,
                      }}
                      summary={() =>
                        renderClienteSignedSummary({
                          totals: otros.totals,
                          label: "Neto otros (cliente)",
                          ...summaryPos(otrosCols, "monto"),
                        })
                      }
                    />
                  </Card>
                ),
              },
              {
                key: "arriendos",
                label: "Arriendos",
                children: (
                  <Card
                    title="Arriendos"
                    extra={
                      <Space>
                        {/* Round 2026-08-06 — "cuál está apretado" no se
                            entendía. Solo "Resumida" traía ícono, y un ícono
                            atrae la vista igual que un estado activo: el ojo
                            leía la opción decorada como la seleccionada, fuera
                            o no la vigente. Ahora las dos llevan ícono (la
                            asimetría era el problema), va rotulado "Vista:"
                            para que se lea como un control y no como dos
                            botones sueltos, y la opción activa se refuerza por
                            CSS en `shared-page.css` — para todas las tablas del
                            sistema, no solo esta. */}
                        <Typography.Text type="secondary">Vista:</Typography.Text>
                        <Segmented<ViewMode>
                          size="middle"
                          className="segmented-claro"
                          onChange={(v) => cambiarVista(v as ViewMode)}
                          value={viewMode}
                          options={[
                            {
                              label: "Resumida",
                              value: "resumida",
                              icon: <ColumnHeightOutlined />,
                            },
                            {
                              label: "Detallada",
                              value: "detallada",
                              icon: <TableOutlined />,
                            },
                          ]}
                        />
                        {!isMobile && viewMode === "detallada" && ColumnChooser}
                        <Statistic
                          title={totalLabelFor("arriendos")}
                          value={totalArriendos}
                          precision={0}
                          formatter={(v) => money(Number(v))}
                        />
                      </Space>
                    }
                  >
                    {isMobile ? (
                      <List
                        loading={arr.loading}
                        dataSource={arriendosVisibles}
                        renderItem={(r: any) => (
                          <Card
                            size="small"
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                {r.id != null && (
                                  <EstadiaCodeTag id={r.id} />
                                )}
                                <span>
                                  {formatDate(r.ingreso)} →{" "}
                                  {formatDate(r.salida)}
                                </span>
                              </div>
                            }
                          >
                            <div>
                              <b>Plataforma:</b> {formatPlataforma(r.plataforma)}
                            </div>
                            <div>
                              <b>Huésped:</b> {r.nombreHuesped || "—"}
                            </div>
                            <div
                              style={{
                                fontSize: 16,
                                fontWeight: 700,
                                marginTop: 6,
                              }}
                            >
                              Ingresos:{" "}
                              {money(
                                r.totalPropietario ??
                                  r.reservatotalpropietario ??
                                  0,
                              )}
                            </div>
                            {/* Botón “ver detalle” opcional */}
                          </Card>
                        )}
                      />
                    ) : (
                      <Table
                        rowKey="id"
                        loading={arr.loading}
                        dataSource={arriendosVisibles}
                        columns={columnsToUse}
                        // Esta tabla no tenía selector de filas por página NI
                        // total: solo el "1" del paginador. Era la única de las
                        // cinco pestañas sin el control, y justo la principal.
                        pagination={paginacionServidor({
                          page: arr.page,
                          size: arr.size,
                          total: arr.total,
                          etiqueta: "arriendo",
                          onChange: arr.onPaginate,
                        })}
                        scroll={{ x: "max-content", y: 560 }}
                        size="middle"
                      />
                    )}
                  </Card>
                ),
              },
            ]}
          />
        </div>

        {/* Resumen global */}
        <Card size="small" title="Resumen financiero" style={{ marginTop: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "6px 8px", fontWeight: 600 }}>
                  Subtotal por arriendos
                </td>
                <td
                  style={{
                    padding: "6px 8px",
                    textAlign: "right",
                    color: "#389e0d",
                    fontWeight: 600,
                  }}
                >
                  {money(totalArriendos)}
                </td>
              </tr>
              <tr>
                <td
                  colSpan={2}
                  style={{ borderTop: "1px solid #f0f0f0", padding: 0 }}
                />
              </tr>
              {/*
                Aseos / reparaciones / otros: pueden tener ingreso del cliente
                (raro pero válido — ej. AF le devuelve algo, o un cargo se
                redirige a su favor) o ser neutros (intermediario AF↔aseador
                que no toca al cliente). Por eso usamos el neto signado del
                backend: `ingresoCliente − egresoCliente`. Productos viene de
                `inventory_transaction` (no tiene `pagado_por`/`pagado_a`
                JSONB) y siempre se modela como egreso puro del cliente.
                Ver docs/flujo-financiero.md §10.5.
              */}
              {(() => {
                // Aseos = aseos de calendario: solo pueden ser egreso para el
                // cliente (cuando él los paga, tipo=1). El backend devuelve
                // `egresoCliente`; el neto es su negativo. Round 2026-06-05.
                const aseosNeto = -Number(aseos.totals?.egresoCliente || 0);
                // Lavandería (AC7) = lavados aledaños que paga el cliente: solo
                // egreso (como aseos). El neto es su negativo.
                const lavadosNeto = -Number(lavados.totals?.egresoCliente || 0);
                // Otros = TODOS los flujos manuales (pueden ser ingreso o
                // egreso para el cliente → neto firmado).
                const otrosNeto =
                  Number(otros.totals?.ingresoCliente || 0) -
                  Number(otros.totals?.egresoCliente || 0);
                const rows = [
                  {
                    label: "Subtotal aseos (neto cliente)",
                    value: aseosNeto,
                    isEgreso: false,
                  },
                  {
                    label: "Subtotal lavandería (neto cliente)",
                    value: lavadosNeto,
                    isEgreso: false,
                  },
                  {
                    label: "Subtotal productos",
                    value: productos.totals?.monto || 0,
                    isEgreso: true,
                  },
                  {
                    label: "Subtotal otros (neto cliente)",
                    value: otrosNeto,
                    isEgreso: false,
                  },
                ];
                const total =
                  totalArriendos +
                  aseosNeto +
                  lavadosNeto -
                  (productos.totals?.monto || 0) +
                  otrosNeto;
                return (
                  <>
                    {rows.map((row) => {
                      const display = row.value;
                      const color = row.isEgreso
                        ? "#cf1322"
                        : row.value >= 0
                          ? "#389e0d"
                          : "#cf1322";
                      const prefix = row.isEgreso
                        ? ""
                        : row.value > 0
                          ? "+"
                          : "";
                      return (
                        <tr key={row.label}>
                          <td style={{ padding: "4px 8px" }}>{row.label}</td>
                          <td
                            style={{
                              padding: "4px 8px",
                              textAlign: "right",
                              color,
                            }}
                          >
                            {prefix}
                            {money(display)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td
                        colSpan={2}
                        style={{ borderTop: "1px solid #f0f0f0", padding: 0 }}
                      />
                    </tr>
                    <tr>
                      <td style={{ padding: "8px", fontWeight: 700, fontSize: 15 }}>
                        Total general
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 15,
                          color: total >= 0 ? "#389e0d" : "#cf1322",
                        }}
                      >
                        {money(total)}
                      </td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </Card>
      </main>
    </Layout>
  );
};

export default FinanzasClientes;
